import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { QuizQuestion } from '@/components/QuizQuestion';
import { QuizResult } from '@/components/QuizResult';
import { quizQuestions, calculateProfile, quizProfiles } from '@/lib/quiz-data';
import logo from '@/assets/logo.png';

const Quiz = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [showResult, setShowResult] = useState(false);
  const [profileId, setProfileId] = useState<string>('');

  const currentQuestion = quizQuestions[currentQuestionIndex];
  const totalQuestions = quizQuestions.length;
  const progress = ((currentQuestionIndex + 1) / totalQuestions) * 100;

  const handleSelectOption = (optionId: string) => {
    setAnswers({
      ...answers,
      [currentQuestion.id]: optionId
    });
  };

  const handleNext = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      // Calculate profile and show result
      const profile = calculateProfile(answers);
      setProfileId(profile);
      setShowResult(true);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const canProceed = answers[currentQuestion.id] !== undefined;

  if (showResult) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-snow via-background to-pine-dark/5 flex flex-col items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <div className="text-center mb-8">
            <img 
              src={logo} 
              alt="TreeRated10 / Árvore Nota 10" 
              className="w-32 h-32 mx-auto mb-4 object-contain"
            />
          </div>
          <QuizResult profile={quizProfiles[profileId]} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-snow via-background to-pine-dark/5 flex flex-col items-center justify-center p-4">
      {/* Decorative elements */}
      <div className="absolute top-10 left-10 text-6xl animate-pulse opacity-30">🎄</div>
      <div className="absolute top-32 right-16 text-4xl animate-pulse opacity-20" style={{ animationDelay: '1s' }}>⭐</div>
      <div className="absolute bottom-32 left-20 text-5xl animate-pulse opacity-25" style={{ animationDelay: '0.5s' }}>🎁</div>

      {/* Back button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/')}
        className="absolute top-4 left-4"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        {t('quiz.back')}
      </Button>

      <div className="max-w-2xl w-full space-y-8">
        {/* Header */}
        <div className="text-center space-y-2">
          <img 
            src={logo} 
            alt="TreeRated10 / Árvore Nota 10" 
            className="w-24 h-24 mx-auto mb-4 object-contain"
          />
          <h1 className="text-3xl font-bold text-foreground">
            {t('quiz.title')}
          </h1>
          <p className="text-muted-foreground">
            {t('quiz.subtitle')}
          </p>
        </div>

        {/* Progress bar */}
        <div className="space-y-2">
          <Progress value={progress} className="h-2" />
          <p className="text-xs text-muted-foreground text-center">
            {Math.round(progress)}% {t('quiz.complete')}
          </p>
        </div>

        {/* Question */}
        <QuizQuestion
          question={currentQuestion}
          selectedOption={answers[currentQuestion.id] || null}
          onSelectOption={handleSelectOption}
          questionNumber={currentQuestionIndex + 1}
          totalQuestions={totalQuestions}
        />

        {/* Navigation buttons */}
        <div className="flex gap-4">
          <Button
            variant="outline"
            size="lg"
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
            className="flex-1"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('quiz.prevButton')}
          </Button>

          <Button
            size="lg"
            onClick={handleNext}
            disabled={!canProceed}
            className="flex-1"
          >
            {currentQuestionIndex === totalQuestions - 1 
              ? t('quiz.seeResult')
              : t('quiz.nextButton')
            }
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Quiz;
